/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { ArrowBack, Edit } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import inputStyles from "../../../../assets/styles/components/input.module.css";
import selectStyles from "../../../../assets/styles/components/select.module.css";
import styles from "./CashFlowForm.module.css";

import { formatDate } from "../../../../utils/useMask";
import Button from "../../../../components/Button/Button";

import SelectChartOfAccounts from "./SelectChartOfAccounts";
import SelectCostCenter from "./SelectCostCenter";
import SelectFinanceAccount from "./SelectFinanceAccount";

import {
    getMoviment,
    patchMoviments,
    postMoviments,
    getFinanceAccounts,
} from "../../../../services/cashFlow";
import type { movimentType } from "../../../../types/cashFlow";
import FinanceDocuments from "./FinanceDocuments";
import FinanceLaunch from "./FinanceLaunch";

const CashFlowForm = () => {
    const navigate = useNavigate();
    const { uid } = useParams();
    const queryClient = useQueryClient();

    const isDetails = !!uid;

    const [isEditMode, setIsEditMode] = useState(!uid);

    const inputClassName = isEditMode ? inputStyles.input : `${inputStyles.input} ${styles.inputViewMode}`;
    const selectClassName = isEditMode ? `${inputStyles.input} ${selectStyles.select}` : `${inputStyles.input} ${selectStyles.select} ${styles.inputViewMode}`;

    const [value, setValue] = useState<movimentType>({
        data_movimentacao: new Date().toISOString().split("T")[0],
        valor: "",
        tipo: "entrada",
        descricao: "",
        uid_conta_financeira: "",
        uid_financeiro_lancamento: null,
        uid_plano_contas: "",
        uid_centro_custo: "",
        origem: "manual",
    });

    const { data: fetchedData, isLoading } = useQuery({
        queryKey: ["getMoviment", uid],
        queryFn: () => getMoviment(uid || ""),
        enabled: !!uid,
    });

    // Para resolver o nome da conta financeira no modo visualização
    const { data: financeAccountsList = [] } = useQuery({
        queryKey: ["getFinanceAccounts"],
        queryFn: getFinanceAccounts,
    });

    const getContaFinanceiraNome = (uid: string) => {
        if (!uid) return "—";
        const conta = (financeAccountsList as any[]).find((c: any) => c.uid_conta_financeira === uid);
        return conta?.nome || uid;
    };

    const handleResetValues = () => {
        if (fetchedData && Object.keys(fetchedData).length > 0) {
            const {
                data_movimentacao,
                valor,
                tipo,
                descricao,
                uid_conta_financeira,
                uid_financeiro_lancamento,
                uid_plano_contas,
                uid_centro_custo,
                origem,
                lancamento,
            } = fetchedData;

            const definedOrigin = (origem || lancamento?.origem || "manual").toLowerCase();

            setValue({
                data_movimentacao: formatDate(data_movimentacao) || null,
                valor: valor,
                tipo,
                descricao,
                uid_conta_financeira,
                uid_financeiro_lancamento,
                uid_plano_contas,
                uid_centro_custo,
                origem: definedOrigin,
            });
        }
    };

    useEffect(() => {
        handleResetValues();
    }, [fetchedData]);

    const { mutate, isPending } = useMutation({
        mutationFn: async (data: movimentType) => {
            if (uid) {
                return await patchMoviments({
                    uid_financeiro_movimentacao: uid,
                    ...data,
                });
            } else {
                return await postMoviments(data);
            }
        },
        onSuccess: (res: any) => {
            queryClient.invalidateQueries({ queryKey: ["getMovimentsCashFlow"] });
            
            if (uid) {
                queryClient.invalidateQueries({ queryKey: ["getMoviment", uid] });
            }

            toast.success("Salvo com sucesso");
            setIsEditMode(false);

            if (!uid) {
                const novoUid = res?.data?.uid_financeiro_movimentacao || res?.uid_financeiro_movimentacao || res?.id;
                if (novoUid) {
                    navigate(`/agro/financialHub/cashFlow/edit/${novoUid}`, { replace: true });
                } else {
                    setTimeout(() => navigate(-1), 1000);
                }
            }
        },
        onError: (err: any) => {
            // Extrai mensagem limpa mesmo quando o backend devolve HTML de erro
            const raw = err?.message ?? err ?? "Erro ao salvar movimentação";
            const isHtml = typeof raw === "string" && raw.trim().startsWith("<");
            if (isHtml) {
                // Tenta extrair o texto do <pre> no HTML de erro do Express
                const match = raw.match(/<pre>([\s\S]*?)<\/pre>/);
                const extracted = match ? match[1].replace(/&nbsp;/g, " ").replace(/<br\s*\/?>/g, "\n").split("\n")[0].trim() : "Erro interno no servidor";
                toast.error(extracted);
            } else {
                toast.error(raw);
            }
        },
    });

    const handleInput = (e: string, valueName: string) => {
        const v = e;
        if (valueName === "descricao" && v.length > 200) return;
        setValue((prev) => ({ ...prev, [valueName]: v }));
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleToggleEditMode = () => {
        if (isEditMode) {
            handleResetValues();
        }
        setIsEditMode((prev) => !prev);
    };

    const handleCancel = () => {
        if (!uid) {
            navigate(-1);
        } else {
            setIsEditMode(false);
            handleResetValues();
        }
    };

    const submit = (e: FormEvent) => {
        e.preventDefault();

        // Validação frontend antes de chamar a API
        const erros: string[] = [];
        if (!value.uid_conta_financeira) erros.push("Conta Financeira");
        if (!value.valor || Number(value.valor) <= 0) erros.push("Valor");
        if (!value.data_movimentacao) erros.push("Data");

        if (erros.length > 0) {
            toast.error(`Preencha os campos obrigatórios: ${erros.join(", ")}`);
            return;
        }

        mutate(value);
    };

    const labelS: React.CSSProperties = { fontSize: "0.72rem", color: "#888", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "block" };
    const labelE: React.CSSProperties = { ...labelS, color: "#00b37e" };
    const inpE: React.CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #00b37e", outline: "none", fontSize: "0.9rem", background: "#f0fff8", boxSizing: "border-box" as const };
    const inpD: React.CSSProperties = { ...inpE, background: "#f0f0f0", color: "#888", cursor: "not-allowed" };
    const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "20px 24px", padding: "16px 4px" };
    const divider: React.CSSProperties = { borderTop: "1px solid #eee", margin: "0 0 20px" };
    const valS: React.CSSProperties = { fontWeight: 500, fontSize: "0.95rem", color: "#222" };
    const ViewF = ({ label, children }: { label: string; children: React.ReactNode }) => (
      <div><span style={labelS}>{label}</span><div style={valS}>{children || "—"}</div></div>
    );
    const formatBRL = (v: any) => Number(v) > 0 ? Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—";
    const fmtDate = (d: string | null) => { if (!d) return "—"; const [y,m,day] = d.split("-"); return `${day}/${m}/${y}`; };

    return (
        <div className={styles.containerPage}>
            <div className={styles.headerPage}>
                <h1>{uid ? "Movimentação" : "Nova Movimentação"}</h1>
                <div className={styles.headerButtons}>
                    {uid && (
                        <Button variant="contain" title={isEditMode ? "Cancelar edição" : "Editar Movimentação"} onClick={handleToggleEditMode}>
                            <Edit />
                        </Button>
                    )}
                    <Button variant="contain" onClick={handleGoBack} type="button" title="Voltar">
                        <ArrowBack />
                    </Button>
                </div>
            </div>

            <div className={styles.formContainer}>
                <div className={styles.formHeader}><h2>DADOS DA MOVIMENTAÇÃO</h2></div>
                <div className={styles.formContent}>
                    <form onSubmit={submit}>
                        {isLoading && isDetails ? <p>Carregando dados...</p> : (
                            <>
                                {/* Seção 1: Dados principais */}
                                <div style={{ marginBottom: 20 }}>
                                    {isEditMode ? (
                                        <div style={grid}>
                                            <div>
                                                <label style={labelE}>Data</label>
                                                <input type="date" style={inpE} value={value.data_movimentacao as string}
                                                    onChange={(e) => handleInput(e.target.value, "data_movimentacao")} />
                                            </div>
                                            <div>
                                                <label style={labelE}>Tipo</label>
                                                <select style={inpE} value={value.tipo} onChange={(e) => handleInput(e.target.value, "tipo")}>
                                                    <option value="entrada">Entrada</option>
                                                    <option value="saida">Saída</option>
                                                    <option value="transferencia">Transferência</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label style={labelE}>Valor</label>
                                                <NumericFormat className={inputStyles.input} style={{ borderColor: "#00b37e", background: "#f0fff8" }}
                                                    placeholder="R$ 0,00" thousandSeparator="." decimalSeparator="," prefix="R$ "
                                                    decimalScale={2} fixedDecimalScale allowNegative={false} value={value.valor}
                                                    onValueChange={(v) => setValue((prev) => ({ ...prev, valor: v.floatValue !== undefined ? String(v.floatValue) : "" }))} />
                                            </div>
                                            <div>
                                                <SelectFinanceAccount handleChangeAccount={(e) => handleInput(e, "uid_conta_financeira")} value={value.uid_conta_financeira} disabled={false} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={grid}>
                                            <ViewF label="Data">{fmtDate(value.data_movimentacao)}</ViewF>
                                            <ViewF label="Tipo">
                                                <span style={{ padding: "3px 12px", borderRadius: 20, fontSize: "0.82rem", fontWeight: 700,
                                                    background: value.tipo === "entrada" ? "#d4edda" : value.tipo === "saida" ? "#f8d7da" : "#cfe2ff",
                                                    color: value.tipo === "entrada" ? "#155724" : value.tipo === "saida" ? "#b65f68" : "#084298" }}>
                                                    {value.tipo === "entrada" ? "Entrada" : value.tipo === "saida" ? "Saída" : "Transferência"}
                                                </span>
                                            </ViewF>
                                            <ViewF label="Valor"><strong>{formatBRL(value.valor)}</strong></ViewF>
                                            <ViewF label="Conta Financeira">{getContaFinanceiraNome(value.uid_conta_financeira)}</ViewF>
                                        </div>
                                    )}
                                </div>

                                <div style={divider} />

                                {/* Seção 2: Classificação */}
                                <div style={{ marginBottom: 20 }}>
                                    <div style={grid}>
                                        <div>
                                            <SelectChartOfAccounts value={value.uid_plano_contas} handleChangeChartOfAccount={(e) => handleInput(e, "uid_plano_contas")} type="ambos" disabled={!isEditMode} />
                                        </div>
                                        <div>
                                            <SelectCostCenter value={value.uid_centro_custo || ""} handleChangeCostCenter={(e) => handleInput(e, "uid_centro_custo")} disabled={!isEditMode} />
                                        </div>
                                    </div>
                                </div>

                                <div style={divider} />

                                {/* Seção 3: Descrição */}
                                <div style={{ marginBottom: 20 }}>
                                    {isEditMode ? (
                                        <div>
                                            <label style={labelE}>Descrição</label>
                                            <textarea style={{ ...inpE, minHeight: 80, resize: "vertical" } as any}
                                                value={value.descricao} placeholder="Descrição da movimentação" rows={2}
                                                onChange={(e) => handleInput(e.target.value, "descricao")} />
                                        </div>
                                    ) : (
                                        <ViewF label="Descrição">{value.descricao || "Nenhuma descrição."}</ViewF>
                                    )}
                                </div>

                                {isEditMode && (
                                    <div className={styles.buttonSave}>
                                        <Button variant="contain" type="submit" disabled={isPending}>
                                            {isPending ? "Salvando..." : "Salvar"}
                                        </Button>
                                        {uid && (
                                            <Button variant="outlined" type="button" onClick={handleCancel} disabled={isPending} className={styles.cancelButton}>
                                                Cancelar
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </form>
                </div>
            </div>
            {!isLoading && <FinanceLaunch data={fetchedData?.lancamento} />}
            <FinanceDocuments
                uid_movimentacao={uid}
                uid_lancamento={fetchedData?.uid_financeiro_lancamento || fetchedData?.lancamento?.uid_financeiro_lancamento}
                isEditing={!!uid}
            />
        </div>
    );
};

export default CashFlowForm;